import { renderRefusal } from "../view/refusal.js";
/**
 * Table de comparaison.
 *
 * Ce n'est pas un repli pour lecteurs d'écran, c'est **l'équivalent complet du
 * graphe** (SPEC §3.4). Toute conclusion qu'on peut tirer de la figure doit
 * pouvoir se tirer d'ici : mesures avec incertitude, prédiction de chaque
 * modèle, écart en sigmas, et le compte d'hypothèses libres — y compris celui
 * du modèle qu'on ne calcule pas.
 *
 * Le module n'énonce aucun verdict : il n'a pas de vocabulaire pour ça.
 */

import type { ShadowComparison, ShadowModelComparison } from "../core/comparison.js";
import { valueIn } from "../core/units.js";
import { fr } from "../i18n/fr.js";
import { escape, escapeAttribute, formatInstant, formatNumber, formatSigma } from "../view/format.js";

function scrollable(label: string, table: string): string {
  return [
    `<div class="verlet-table-scroll" role="region" tabindex="0"`,
    ` aria-label="${escapeAttribute(label)}">`,
    table,
    `</div>`,
  ].join("");
}


function measurementRows(comparison: Extract<ShadowComparison, { ok: true }>): string {
  const computed = comparison.models.filter((model) => model.calculation === "computed");

  return comparison.observations
    .map((observation) => {
      const cells = [
        `<th scope="row">${escape(formatInstant(observation.instant))}</th>`,
        `<td>${escape(observation.siteId)}</td>`,
        `<td>${escape(
          `${formatNumber(valueIn(observation.observed, "m"), 3)} ± ${formatNumber(
            valueIn(observation.uncertainty, "m"),
            3,
          )} m`,
        )}</td>`,
      ];

      for (const model of computed) {
        const prediction = observation.predictions.get(model.model.id);
        if (prediction?.kind !== "predicted" || prediction.inSigma === undefined) {
          cells.push(
            `<td>${escape(prediction?.kind === "absent" ? prediction.reason : fr.noPrediction)}</td>`,
            `<td>—</td>`,
          );
          continue;
        }
        cells.push(
          `<td>${escape(`${formatNumber(valueIn(prediction.value, "m"), 3)} m`)}</td>`,
          `<td>${escape(formatSigma(prediction.inSigma))}</td>`,
        );
      }

      return `<tr>${cells.join("")}</tr>`;
    })
    .join("");
}

function header(computed: readonly ShadowModelComparison[]): string {
  const columns = [
    `<th scope="col">${escape(fr.columnDate)}</th>`,
    `<th scope="col">${escape(fr.columnSite)}</th>`,
    `<th scope="col">${escape(fr.columnMeasured)}</th>`,
  ];
  for (const model of computed) {
    columns.push(
      `<th scope="col">${escape(model.model.label)}</th>`,
      `<th scope="col">${escape(fr.columnGap)}</th>`,
    );
  }
  return `<tr>${columns.join("")}</tr>`;
}

/**
 * Bilan par modèle. Le compte d'hypothèses libres y figure pour **tous** les
 * modèles, y compris celui qui n'est pas calculé : c'est son seul chiffre, et
 * c'est celui qui porte l'argument.
 */
function summary(comparison: Extract<ShadowComparison, { ok: true }>): string {
  const rows = comparison.models
    .map((model) => {
      const gap =
        model.meanAbsoluteSigma === undefined
          ? escape(fr.notComputed)
          : escape(`${formatNumber(valueIn(model.meanAbsoluteSigma, "1"), 1)} σ`);
      return [
        `<tr>`,
        `<th scope="row">${escape(model.model.label)}</th>`,
        `<td>${gap}</td>`,
        `<td>${escape(formatNumber(valueIn(model.freeHypotheses, "1"), 0))}</td>`,
        `</tr>`,
      ].join("");
    })
    .join("");

  return scrollable(fr.summaryCaption, [
    `<table class="verlet-summary">`,
    `<caption>${escape(fr.summaryCaption)}</caption>`,
    `<thead><tr>`,
    `<th scope="col">${escape(fr.columnModel)}</th>`,
    `<th scope="col">${escape(fr.columnMeanGap)}</th>`,
    `<th scope="col">${escape(fr.columnFreeHypotheses)}</th>`,
    `</tr></thead>`,
    `<tbody>${rows}</tbody>`,
    `</table>`,
  ].join(""));
}

export function renderComparisonTable(comparison: ShadowComparison): string {
  if (!comparison.ok) return renderRefusal(comparison.reasons);

  const computed = comparison.models.filter((model) => model.calculation === "computed");

  return [
    `<div class="verlet-tables">`,
    scrollable(
      fr.measurementsCaption,
      [
        `<table class="verlet-measurements">`,
        `<caption>${escape(fr.measurementsCaption)}</caption>`,
        `<thead>${header(computed)}</thead>`,
        `<tbody>${measurementRows(comparison)}</tbody>`,
        `</table>`,
      ].join(""),
    ),
    summary(comparison),
    `</div>`,
  ].join("");
}
